//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { SamplingCatalogProduct } from '@apis/campaign-requests/sampling/products/samplingProductsApiV2Model';
import { CatalogProductInfo } from '@campaigns/components/shared/product-details-modal/details/CatalogProductInfo';
import Icon from '@components/atoms/icon/Icon';
import Spinner from '@components/atoms/spinner/Spinner';
import { BodyText } from '@components/atoms/text/Text';
import { isDefined } from '@utils/filterPredicates';

interface ProductStatusMessageProps {
  isCheckingCatalog: boolean;
  catalogProduct?: SamplingCatalogProduct;
  hasImportedProduct: boolean;
  onSelectProduct: (product: SamplingCatalogProduct) => void;
}

interface StatusInfo {
  icon: React.ReactNode;
  message: string;
}

const getStatusInfo = (
  isCheckingCatalog: boolean,
  hasImportedProduct: boolean,
  catalogProduct?: SamplingCatalogProduct,
): StatusInfo => {
  if (isCheckingCatalog) {
    return {
      icon: <StyledSpinner />,
      message: 'Checking if Product ID exists in your catalog',
    };
  }

  if (hasImportedProduct) {
    return {
      icon: <StyledCheckmarkIcon name='checkmark' />,
      message: 'Product imported successfully',
    };
  }

  if (catalogProduct) {
    return {
      icon: <StyledErrorIcon name='error' />,
      message: 'A product with this ID exists in your catalog',
    };
  }

  return {
    icon: <StyledCheckmarkIcon name='checkmark' />,
    message: 'Product ID is unique',
  };
};

export const ProductStatusMessage = ({
  catalogProduct,
  hasImportedProduct,
  isCheckingCatalog,
  onSelectProduct,
}: ProductStatusMessageProps) => {
  const { icon, message } = getStatusInfo(
    isCheckingCatalog,
    hasImportedProduct,
    catalogProduct,
  );

  return (
    <>
      <StatusMessageContainer>
        {icon}
        <BodyText>{message}</BodyText>
      </StatusMessageContainer>

      {isDefined(catalogProduct) && (
        <CatalogProductInfo
          onSelectProduct={onSelectProduct}
          product={catalogProduct}
        />
      )}
    </>
  );
};

const StatusMessageContainer = styled.div(
  ({ theme }) => `
  display: flex;
  align-items: center;
  gap: ${theme.spacings[1]};
  margin-top: ${theme.spacings[1]};
  `,
);

const StyledSpinner = styled(Spinner)(
  ({ theme }) => `
    display: inline-block;
    width: ${theme.spacings[3]};
    height: ${theme.spacings[3]};
  `,
);

const StyledCheckmarkIcon = styled(Icon)`
  color: ${({ theme }) => theme.colors.aliases.success};
`;

const StyledErrorIcon = styled(Icon)`
  color: ${({ theme }) => theme.colors.aliases.error};
`;
